import React, { useMemo } from "react";
import { Box, Grid, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useStyles } from "./TopFiveFunctionalLocations.styles";
import { BarsChartFunctionalLocations } from "components/BarsChartFunctionalLocations/BarsChartFunctionalLocations.view";
import { IChartBar, IGraphProps } from "./TopFiveFunctionalLocationsInterfaces";
import { NoStatsData } from "../NoStatsData/NoStatsData.view";
import { handleNameTranslation, translateDefaultEntity } from "utilities/localesUtilities";
import { DefaultTranslationFieldsInTable } from "constants/constants";

export const TopFiveFunctionalLocations = ({ downtimeEvents, showTitle, countryCode }: IGraphProps) => {
    const { t, i18n } = useTranslation();
    const classes = useStyles();

    const filterFailureTypesByFunctionalLocation = useMemo(() => {
        const groups: {
            [key: string]: {
                country_code: string;
                functional_location_id: number;
                functional_location_name: string;
                functional_location_total_occurrences: number;
                functional_location_total_minutes: number;
                functional_location_failure_types: {
                    failure_type_id: number;
                    failure_type_name: string;
                    color_code: string;
                    total: number;
                }[];
            };
        } = {};

        downtimeEvents &&
            downtimeEvents
                .filter((downtimeEvent) => downtimeEvent.functional_location !== null)
                .forEach((downtimeEvent) => {
                    const groupName = handleNameTranslation(
                        i18n.language,
                        downtimeEvent.functional_location.translation.translation_names,
                    );
                    const group = groups[groupName] || {
                        functional_location_id: downtimeEvent.functional_location.functional_location_id,
                        functional_location_name: groupName,
                        functional_location_total_occurrences: 0,
                        functional_location_total_minutes: 0,
                        functional_location_failure_types: [],
                    };

                    group.country_code = countryCode!;
                    group.functional_location_total_minutes += downtimeEvent.validTime;

                    const existingFailureTypeIndex = group.functional_location_failure_types.findIndex(
                        (failureType) => failureType.failure_type_id === downtimeEvent.failure_type.failure_type_id,
                    );

                    if (existingFailureTypeIndex !== -1) {
                        group.functional_location_failure_types[existingFailureTypeIndex].total +=
                            downtimeEvent.validTime;
                    } else {
                        group.functional_location_failure_types.push({
                            failure_type_id: downtimeEvent.failure_type.failure_type_id,
                            failure_type_name: translateDefaultEntity(
                                i18n.language,
                                downtimeEvent.failure_type,
                                "name",
                                DefaultTranslationFieldsInTable.FAILURE_TYPE,
                            ),
                            color_code: downtimeEvent.failure_type.color_code,
                            total: downtimeEvent.validTime,
                        });
                    }

                    groups[groupName] = group;
                });

        return Object.values(groups);
    }, [downtimeEvents, countryCode, i18n.language]);

    const barsChartData = useMemo(() => {
        const sortedTopFiveFunctionalLocations = filterFailureTypesByFunctionalLocation
            .sort((a, b) => {
                return b.functional_location_total_minutes - a.functional_location_total_minutes;
            })
            .slice(0, 5)
            .reverse()
            .map((stat) => {
                const chartBar: IChartBar = {
                    functionalLocation: stat.functional_location_name,
                    countryCode: stat.country_code,
                };

                stat.functional_location_failure_types.forEach((item) => {
                    chartBar[`${item.failure_type_name}Color`] = item.color_code;
                    chartBar[`${item.failure_type_name}`] = item.total < 60 ? 1 : Math.round(item.total / 60);
                });

                return chartBar;
            });

        return sortedTopFiveFunctionalLocations;
    }, [filterFailureTypesByFunctionalLocation]);

    return (
        <Box className={classes.container}>
            {showTitle && (
                <Typography variant="h1" className={classes.TopFiveFunctionalLocationsTitle}>
                    {t("HMI.top_five_functional_locations.title")}
                </Typography>
            )}
            {filterFailureTypesByFunctionalLocation && filterFailureTypesByFunctionalLocation.length > 0 ? (
                <Grid container className={classes.graphContainer}>
                    <Grid item xs={12} className={classes.graphGridItemContainer}>
                        <BarsChartFunctionalLocations data={barsChartData} />
                    </Grid>
                </Grid>
            ) : (
                <NoStatsData />
            )}
        </Box>
    );
};
